import type {
	CloudRecording,
	CloudRecordingClassificationObject,
	CloudRecordingVideo,
} from '../types/cloud-recordings';
import { RecordingMediaType } from '../types/cloud-recordings';
import type { LabelProps, ListItemPropsTypes } from '@Shelly/shelly-design-system/index';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { app } from 'src/stores/session/native-bridge';
import { dateFormat } from 'src/helpers/date_helper';
import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';

const _format = get(format);

/**
 * Determines whether a recording is an HLS stream, which must be played through
 * hls.js rather than a plain `<video>` element.
 *
 * @param recording - The cloud recording to inspect.
 * @returns `true` when the recording is a video whose URL is an `.m3u8` playlist.
 */
export const isHlsRecording = (recording: CloudRecording | null | undefined): boolean => {
	if (!recording || recording.type !== RecordingMediaType.VIDEO) return false;
	const videoUrl = (recording as CloudRecordingVideo)?.videoUrl;
	return typeof videoUrl === 'string' && videoUrl.split('?')[0].toLowerCase().endsWith('.m3u8');
};

export type BarColor = NonNullable<ListItemPropsTypes['progress']['color']>;

const DEFAULT_BAR_COLOR: BarColor = 'white';

type ObjectLabelDef = {
	translationKey: string;
	defaultLabel: string;
	icon: string;
	iconColor?: LabelProps['iconColor'];
	barColor: BarColor;
};

const OBJECT_LABELS: Record<string, ObjectLabelDef> = {
	person: {
		translationKey: 'camera_object_person',
		defaultLabel: 'person',
		icon: 'fas fa-person',
		iconColor: '--color-primary',
		barColor: 'primary',
	},
	dog: {
		translationKey: 'camera_object_dog',
		defaultLabel: 'dog',
		icon: 'fas fa-dog',
		iconColor: '--color-electric-vehicle',
		barColor: 'electric-vehicle',
	},
	cat: {
		translationKey: 'camera_object_cat',
		defaultLabel: 'cat',
		icon: 'fas fa-cat',
		iconColor: '--color-entertainment',
		barColor: 'entertainment',
	},
	car: {
		translationKey: 'camera_object_car',
		defaultLabel: 'car',
		icon: 'fas fa-car',
		iconColor: '--color-cooking',
		barColor: 'cooking',
	},
	vehicle: {
		translationKey: 'camera_object_vehicle',
		defaultLabel: 'vehicle',
		icon: 'fas fa-car',
		iconColor: '--color-cooking',
		barColor: 'cooking',
	},
	truck: {
		translationKey: 'camera_object_truck',
		defaultLabel: 'truck',
		icon: 'fas fa-truck',
		iconColor: '--color-heating',
		barColor: 'heating',
	},
};

export const getObjectBarColor = (name?: string): BarColor =>
	(name ? OBJECT_LABELS[name.toLowerCase()] : undefined)?.barColor ?? DEFAULT_BAR_COLOR;

const DEFAULT_OBJECT_ICON = 'fas fa-tag';

const toObjectLabelProps = (def: ObjectLabelDef | undefined, value: string): LabelProps => {
	if (!def) {
		return { value, icon: DEFAULT_OBJECT_ICON };
	}
	const base: LabelProps = { value, icon: def.icon };
	const iconColor = def.iconColor;
	return iconColor !== undefined && iconColor !== '' ? { ...base, iconColor } : base;
};

const getObjectLabels = (objects: CloudRecordingClassificationObject[]): LabelProps[] => {
	const counts = new Map<string, number>();
	for (const obj of objects) {
		const name = obj?.name?.toLowerCase();
		if (!name) continue;
		counts.set(name, (counts.get(name) ?? 0) + 1);
	}

	return Array.from(counts, ([name, count]) => {
		const def = OBJECT_LABELS[name];
		const value = def ? _format(def.translationKey, { default: def.defaultLabel }) : name;
		return toObjectLabelProps(def, count > 1 ? `${count}x ${value}` : value);
	});
};

export type DetectedObjectRow = {
	id: string;
	icon: string;
	name: string;
	percent: number;
	barColor: BarColor;
};

export type DetectedObjectBox = {
	id: string;
	leftPct: number;
	topPct: number;
	widthPct: number;
	heightPct: number;
	label: string;
	color: string;
};

/** Shared id between list rows and bounding boxes so they can be cross-referenced. */
export const getDetectedObjectId = (rawIndex: number): string => `obj-${rawIndex}`;

export const getDetailedObjectRows = (
	objects: CloudRecordingClassificationObject[],
): DetectedObjectRow[] =>
	(objects ?? [])
		.map((o, idx) => {
			if (!o?.name) return null;
			const def = OBJECT_LABELS[o.name.toLowerCase()];
			const name = def ? _format(def.translationKey, { default: def.defaultLabel }) : o.name;
			const percent = Math.round((o.score ?? 0) * 100);
			const row: DetectedObjectRow = {
				id: getDetectedObjectId(idx),
				icon: def?.icon ?? DEFAULT_OBJECT_ICON,
				name,
				percent,
				barColor: def?.barColor ?? DEFAULT_BAR_COLOR,
			};
			return row;
		})
		.filter((r): r is DetectedObjectRow => r !== null);

const toDetectedObjectBox = (
	obj: CloudRecordingClassificationObject,
	rawIndex: number,
	naturalWidth: number,
	naturalHeight: number,
): DetectedObjectBox | null => {
	const bb = obj?.boundingBox;
	if (!bb || bb.length !== 4 || !naturalWidth || !naturalHeight) return null;
	const [x1, y1, x2, y2] = bb;
	const left = Math.min(x1, x2);
	const top = Math.min(y1, y2);
	const right = Math.max(x1, x2);
	const bottom = Math.max(y1, y2);
	const width = right - left;
	const height = bottom - top;
	if (width <= 0 || height <= 0) return null;
	const percent = Math.round((obj.score ?? 0) * 100);
	return {
		id: getDetectedObjectId(rawIndex),
		leftPct: (left / naturalWidth) * 100,
		topPct: (top / naturalHeight) * 100,
		widthPct: (width / naturalWidth) * 100,
		heightPct: (height / naturalHeight) * 100,
		label: `${obj.name} ${percent}%`,
		color: `var(--color-${getObjectBarColor(obj.name)})`,
	};
};

export const getDetectedObjectBoxes = (
	objects: CloudRecordingClassificationObject[],
	naturalWidth: number,
	naturalHeight: number,
): DetectedObjectBox[] => {
	if (!naturalWidth || !naturalHeight) return [];
	return (objects ?? [])
		.map((obj, idx) => toDetectedObjectBox(obj, idx, naturalWidth, naturalHeight))
		.filter((b): b is DetectedObjectBox => b !== null);
};

export const getRecordingEventLabel = (recording: CloudRecording) => {
	let labelList: LabelProps[] = [];
	switch (recording.event) {
		case 'motion':
			labelList.push({
				value: _format('motion'),
				icon: 'fas fa-running',
			});
			break;
		case 'image':
			labelList.push({
				value: _format('image'),
				icon: 'fas fa-image',
			});
			break;
		default:
			labelList.push({
				value: recording.event,
				icon: DEFAULT_OBJECT_ICON,
			});
			break;
	}

	labelList.push(...getObjectLabels(recording.classification?.objects ?? []));

	return labelList;
};

type DownloadableMedia = { url: string; extension: string };

/** Resolve the best media source (and its type) for a recording, if any. */
const getDownloadableMedia = (recording: CloudRecording): DownloadableMedia | null => {
	const videoUrl = (recording as CloudRecordingVideo)?.videoUrl;
	if (recording?.type === RecordingMediaType.VIDEO && videoUrl) {
		return { url: videoUrl, extension: 'mp4' };
	}
	if (recording?.type === RecordingMediaType.IMAGE && recording.imageUrls?.large) {
		return { url: recording.imageUrls.large, extension: 'jpg' };
	}
	return null;
};

/**
 * A recording can be downloaded when it has a video or image source URL and is
 * not encrypted (encrypted media would download as undecryptable AES bytes).
 */
export const isDownloadableMedia = (recording: CloudRecording): boolean =>
	!recording?.encrypted && getDownloadableMedia(recording) !== null;

const buildRecordingFilename = (recording: CloudRecording, extension: string): string => {
	const datePart = dateFormat(recording.date, 'Y-m-d_H-i-s');
	const eventPart = (recording.event || 'recording').replace(/[^a-z0-9]+/gi, '-');
	return `ShellyCamera-${eventPart}-${datePart}.${extension}`;
};

/** Download a cloud recording (video or image) to the device. */
export const downloadMedia = async (recording: CloudRecording): Promise<boolean> => {
	if (!isDownloadableMedia(recording)) return false;
	const media = getDownloadableMedia(recording);
	if (!media) return false;

	const authenticatedUrl = await buildCloudMediaUrl(media.url);
	if (!authenticatedUrl) return false;

	const filename = buildRecordingFilename(recording, media.extension);
	const a = document.createElement('a');
	a.setAttribute('target', '_blank');
	a.setAttribute('download', filename);

	// App: hand the authenticated URL to the wrapper to save the file.
	if (get(app)) {
		a.setAttribute('href', authenticatedUrl);
		a.click();
		return true;
	}

	// Browser: fetch as a blob so the click downloads instead of opening a tab.
	const response = await fetch(authenticatedUrl);
	if (!response.ok) return false;

	const blob = await response.blob();
	const windowURL = window.webkitURL || window.URL;
	const url = windowURL.createObjectURL(blob);
	a.setAttribute('href', url);
	a.click();
	windowURL.revokeObjectURL(url);
	return true;
};
